'use client';

import { type FC, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ScreenContent, ScreenFixedTop, ScreenFixedBottom } from '../../../screen';
import { RushMidTabsBar } from '../game/shared/RushMidTabsBar';
import type { RushMidTab } from '../game/RushMidGame';
import { Spinner, EmptyState, Button } from '../../ui';
import { FaRegQuestionCircle } from '../../icons';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import gameTranslations from '../game/RushMid.translations';
import { useRushRaffleRounds } from '../../../queries/useRushRaffleQueries';
import { queryKeys } from '../../../lib/queryClient';
import { useNavigation } from '../../../contexts/NavigationContext';
import { RaffleCard } from './RaffleCard';
import { RaffleRulesSheet } from './RaffleRulesSheet';
import translations from './raffle.translations';

export interface RaffleTabProps {
  tab?: RushMidTab;
  onTab?: (tab: RushMidTab) => void;
  onRequestCreate?: () => void;
}

export const RaffleTab: FC<RaffleTabProps> = ({ tab, onTab, onRequestCreate }) => {
  const { t: tg } = useLocalTranslation(gameTranslations);
  const { t } = useLocalTranslation(translations);
  const queryClient = useQueryClient();
  const { data, isLoading } = useRushRaffleRounds();
  const rounds = data?.rounds ?? [];
  const [rulesOpen, setRulesOpen] = useState(false);
  const navigation = useNavigation();
  const openRound = useCallback((roundId: string) => {
    navigation.navigate('rush-mid-raffle-round', { roundId });
  }, [navigation]);

  const handlePlay = useCallback((ticket: number) => {
    queryClient.setQueryData(queryKeys.rushMidSkins, (old: unknown) =>
      old && typeof old === 'object' ? { ...old, ticket, matchMode: 'ranked' } : old);
    onTab?.('games');
    onRequestCreate?.();
  }, [queryClient, onTab, onRequestCreate]);

  return (
    <>
      <ScreenFixedTop title={tg('lobbyTitle')}>
        {tab && onTab && <RushMidTabsBar activeTab={tab} onChange={onTab} />}
      </ScreenFixedTop>
      <ScreenContent hideLegalSection scrollKey="rush-mid-raffle">
        <div className="flex flex-col gap-3 px-4 pt-3">
          {isLoading ? (
            <div className="flex min-h-40 items-center justify-center">
              <Spinner size="l" />
            </div>
          ) : rounds.length === 0 ? (
            <EmptyState className="mt-6" title={t('emptyTitle')} />
          ) : (
            <div className="flex flex-col gap-3">
              {rounds.map((round) => (
                <RaffleCard key={round.roundId} round={round} onOpen={openRound} onPlay={handlePlay} />
              ))}
            </div>
          )}
        </div>
      </ScreenContent>
      <ScreenFixedBottom className="flex flex-col gap-2 px-4">
        <Button variant="tertiary" size="l" stretched onClick={() => setRulesOpen(true)}>
          <span className="inline-flex items-center gap-1.5"><FaRegQuestionCircle size={15} /> {t('rulesButton')}</span>
        </Button>
      </ScreenFixedBottom>
      <RaffleRulesSheet isOpen={rulesOpen} onClose={() => setRulesOpen(false)} />
    </>
  );
};

export default RaffleTab;
